import { ArrowSquareOut, CheckCircle, X } from '@phosphor-icons/react';
import { plural } from '../findings-display-model';
import type { AutofixResult } from '../findings-types';
import s from '../findings.module.scss';

type AutofixSuccessBannerProps = {
  result: AutofixResult;
  onDismiss: () => void;
};

/**
 * Persistent payoff banner shown after an autofix opens a fix PR. The
 * toast that fires alongside it is transient; this is the surface that
 * keeps the "Fragments fixed your drift" result — and the link to the
 * PR — in view until the user dismisses it.
 */
export function AutofixSuccessBanner({
  result,
  onDismiss,
}: AutofixSuccessBannerProps) {
  const { prUrl, fixedCount, fileCount, prCount } = result;
  const multiPr = prCount > 1;

  return (
    <div className={s.autofixBanner} role="status">
      <CheckCircle size={18} weight="fill" className={s.autofixBannerIcon} />
      <div className={s.autofixBannerBody}>
        <strong>
          Fragments opened {multiPr ? `${prCount} fix PRs` : 'a fix PR'}
        </strong>
        <span>
          Replaced {plural(fixedCount, 'hardcoded value')} with design tokens
          {fileCount > 0 ? ` across ${plural(fileCount, 'file')}` : ''}.
        </span>
      </div>
      <a
        className={s.autofixBannerLink}
        href={prUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        {multiPr ? 'View first PR' : 'View PR'}
        <ArrowSquareOut size={14} />
      </a>
      <button
        type="button"
        className={s.autofixBannerDismiss}
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
